import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import api, { errMsg } from '../lib/api';
import { STATUS_LABELS } from '../lib/shop';
import { PageHead } from './components';

const STATUSES = Object.entries(STATUS_LABELS);

export default function AdminInventory() {
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/inventory');
      const flat = [];
      data.forEach((r) => {
        if (r.variants && r.variants.length) {
          r.variants.forEach((v) => flat.push({
            key: `${r.id}:${v.id}`, id: r.id, variant_id: v.id,
            name: r.name, strength: v.strength, format: r.format, sku: v.sku || r.sku,
            track_stock: r.track_stock, low_stock_threshold: r.low_stock_threshold,
            stock_quantity: v.stock_quantity ?? 0, batch_number: v.batch_number || '',
            status: v.status || 'available', retail_price: v.retail_price ?? 0, active: v.active !== false,
            is_variant: true,
          }));
        } else {
          flat.push({ ...r, key: r.id, variant_id: '', is_variant: false });
        }
      });
      setRows(flat);
      setDirty({});
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const edit = (key, field, value) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
    setDirty((d) => ({ ...d, [key]: true }));
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const updates = rows.filter((r) => dirty[r.key]).map((r) => ({
        id: r.id, variant_id: r.variant_id || undefined,
        stock_quantity: Number(r.stock_quantity) || 0, batch_number: r.batch_number,
        status: r.status, retail_price: Number(r.retail_price) || 0,
        ...(r.is_variant ? {} : { low_stock_threshold: Number(r.low_stock_threshold) || 0 }),
      }));
      const { data } = await api.post('/admin/inventory/bulk', { updates });
      toast.success(`${data.updated} records updated — live on the storefront`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const num = "w-20 border hairline px-2 py-1.5 text-sm font-mono2 focus:outline-none focus:border-[#0B0F12]";

  return (
    <div data-testid="admin-inventory">
      <PageHead title="Inventory" sub="Bulk stock, batch, price and status editor — product rows and per-strength variant rows. No code changes needed.">
        <button data-testid="inventory-save-all" onClick={saveAll} disabled={saving || !Object.keys(dirty).length}
          className="bg-[#0B0F12] text-white px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-widest flex items-center gap-1.5 hover:bg-[#1F8A58] transition-colors disabled:opacity-40">
          <Save className="w-3.5 h-3.5" /> Save {Object.keys(dirty).length > 0 ? `(${Object.keys(dirty).length})` : 'all'}
        </button>
      </PageHead>

      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b hairline">
              {['Product / Strength', 'Price (£)', 'Stock', 'Low-stock alert', 'Batch', 'Status'].map((h) => (
                <th key={h} className="text-left p-3 font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const low = r.track_stock && r.is_variant ? r.stock_quantity <= 0
                : r.track_stock && r.stock_quantity <= r.low_stock_threshold;
              return (
                <tr key={r.key} data-testid={`inventory-row-${r.key}`} className={`border-b hairline last:border-0 ${low ? 'bg-[#FFF7ED]' : ''} ${!r.active ? 'opacity-40' : ''}`}>
                  <td className="p-3">
                    <p className="font-display font-bold uppercase leading-tight">
                      {r.is_variant && <span className="text-[#8A8F95] mr-1.5">↳</span>}
                      {r.name} {r.strength && <span style={{ color: '#E54D2E' }}>{r.strength}</span>}
                    </p>
                    <p className="font-mono2 text-[10px] text-[#8A8F95]">{r.sku} · {r.format}{r.is_variant ? ' · variant' : ''}{!r.track_stock ? ' · stock not tracked' : ''}</p>
                  </td>
                  <td className="p-3"><input data-testid={`inv-price-${r.key}`} type="number" step="0.01" value={r.retail_price} onChange={(e) => edit(r.key, 'retail_price', e.target.value)} className={num} /></td>
                  <td className="p-3">
                    <input data-testid={`inv-stock-${r.key}`} type="number" min="0" value={r.stock_quantity} onChange={(e) => edit(r.key, 'stock_quantity', e.target.value)} className={num} />
                    {low && <span className="ml-2 font-mono2 text-[9px] uppercase tracking-wider text-[#D97706]">{r.stock_quantity === 0 ? 'Out' : 'Low'}</span>}
                  </td>
                  <td className="p-3">
                    {r.is_variant ? <span className="font-mono2 text-xs text-[#8A8F95]">product-level</span> : (
                      <input type="number" min="0" value={r.low_stock_threshold} onChange={(e) => edit(r.key, 'low_stock_threshold', e.target.value)} className={num} />
                    )}
                  </td>
                  <td className="p-3"><input data-testid={`inv-batch-${r.key}`} value={r.batch_number || ''} onChange={(e) => edit(r.key, 'batch_number', e.target.value)} placeholder="—" className="w-32 border hairline px-2 py-1.5 text-sm font-mono2 focus:outline-none focus:border-[#0B0F12]" /></td>
                  <td className="p-3">
                    <select data-testid={`inv-status-${r.key}`} value={r.status} onChange={(e) => edit(r.key, 'status', e.target.value)} className="border hairline px-2 py-1.5 text-xs font-mono2 uppercase focus:outline-none">
                      {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
